/**
 * Component Overrides
 * Customizes default MUI component styles and behaviors
 *
 * Accessibility:
 * - Focus indicators: WCAG 2.4.7 Focus Visible (2px primary outline)
 * - Touch targets: WCAG 2.5.5 Target Size (44x44px minimum)
 */

import { Components, Theme } from '@mui/material/styles';
import { FOCUS_COLOR, FOCUS_WIDTH, FOCUS_OFFSET } from './focusStyles';
import { MIN_TOUCH_TARGET } from './touchTargets';

/**
 * Border radius tokens
 * Consistent rounding across the application
 */
export const borderRadius = {
  none: 0,
  sm: 4, // Inputs, small buttons
  md: 8, // Cards, larger buttons
  lg: 8, // Large cards
  xl: 8, // Modals, dialogs
  full: 9999, // Pills, circular elements
};

/**
 * Spacing tokens
 * 8px grid system
 */
export const spacing = 8;

/**
 * Component overrides
 * Applies custom styling to MUI components
 */
export const components: Components<Theme> = {
  /**
   * MuiContainer - Default to xl maxWidth so pages use the available
   * space next to the sidebar instead of centering in a narrow column.
   */
  MuiContainer: {
    defaultProps: {
      maxWidth: 'xl',
    },
  },

  /**
   * MuiButton - Button component
   * Focus: Standard outline, 2px offset
   * Touch: Minimum 44px height (WCAG 2.5.5)
   */
  MuiButton: {
    defaultProps: {
      disableElevation: false,
    },
    styleOverrides: {
      root: {
        borderRadius: borderRadius.sm,
        textTransform: 'none',
        fontWeight: 600,
        padding: '8px 16px',
        // Touch target (WCAG 2.5.5)
        minHeight: MIN_TOUCH_TARGET,
        // Focus styles (WCAG 2.4.7)
        '&:focus-visible': {
          outline: `${FOCUS_WIDTH}px solid ${FOCUS_COLOR}`,
          outlineOffset: `${FOCUS_OFFSET}px`,
        },
        '&:focus:not(:focus-visible)': {
          outline: 'none',
        },
      },
      sizeLarge: {
        padding: '12px 24px',
        fontSize: '1rem',
        minHeight: 48,
      },
      sizeSmall: {
        padding: '6px 12px',
        fontSize: '0.8125rem',
        minHeight: 36, // Slightly smaller but still accessible
      },
      contained: {
        background: '#6F91B3',
        color: '#0D1218',
        boxShadow: 'none',
        '&:hover': {
          background: '#8AA8C6',
          transform: 'translateY(-1px)',
          boxShadow: '0 8px 24px rgba(111, 145, 179, 0.22)',
        },
        '&:active': {
          color: '#0D1218',
          background: '#5E748A',
        },
        '&.Mui-focusVisible': {
          color: '#0D1218',
        },
      },
    },
  },

  /**
   * MuiCard - Card component
   * Subtle inset top-edge highlight for premium glass-panel effect
   */
  MuiCard: {
    defaultProps: {
      elevation: 1,
    },
    styleOverrides: {
      root: {
        borderRadius: borderRadius.md,
        overflow: 'hidden',
        border: '1px solid rgba(247, 244, 238, 0.12)',
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.18), inset 0 1px 0 rgba(247, 244, 238, 0.03)',
      },
    },
  },

  /**
   * MuiCardContent - Card content
   */
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: '16px',
        '&:last-child': {
          paddingBottom: '16px',
        },
      },
    },
  },

  /**
   * MuiTextField - Text input component
   */
  MuiTextField: {
    defaultProps: {
      variant: 'outlined',
    },
  },

  /**
   * MuiOutlinedInput - Outlined input styling
   */
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: borderRadius.sm,
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: '#6F91B3',
          borderWidth: '2px',
        },
      },
      input: {
        padding: '14px 14px',
        display: 'flex',
        alignItems: 'center',
      },
    },
  },

  /**
   * MuiInputLabel - Input label
   */
  MuiInputLabel: {
    styleOverrides: {
      root: {
        fontWeight: 500,
        // Vertically centre the label within the input
        transform: 'translate(14px, 14px) scale(1)',
        '&.MuiInputLabel-shrink': {
          transform: 'translate(14px, -9px) scale(0.75)',
        },
      },
    },
  },

  /**
   * MuiChip - Chip component
   * Focus: Standard outline for clickable chips
   * Touch: Minimum 32px height (compact but tappable)
   */
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: borderRadius.full,
        fontWeight: 500,
        // Touch target - chips use 32px as acceptable compromise
        height: 32,
        // Focus styles for clickable chips (WCAG 2.4.7)
        '&.MuiChip-clickable:focus-visible': {
          outline: `${FOCUS_WIDTH}px solid ${FOCUS_COLOR}`,
          outlineOffset: `${FOCUS_OFFSET}px`,
        },
        '&.MuiChip-clickable:focus:not(:focus-visible)': {
          outline: 'none',
        },
        // Ensure delete icon has adequate touch target
        '& .MuiChip-deleteIcon': {
          minWidth: 24,
          minHeight: 24,
          margin: '0 4px 0 -4px',
        },
      },
      sizeSmall: {
        height: 24,
        fontSize: '0.75rem',
      },
      sizeMedium: {
        height: 32,
      },
    },
  },

  /**
   * MuiPaper - Paper component
   */
  MuiPaper: {
    styleOverrides: {
      rounded: {
        borderRadius: borderRadius.md,
      },
    },
  },

  /**
   * MuiDialog - Dialog/Modal component
   */
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: borderRadius.xl,
        padding: '8px',
      },
    },
  },

  /**
   * MuiDialogTitle - Dialog title
   */
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontSize: '1.25rem',
        fontWeight: 600,
        padding: '16px 24px',
      },
    },
  },

  /**
   * MuiDialogContent - Dialog content
   */
  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: '16px 24px',
      },
    },
  },

  /**
   * MuiDialogActions - Dialog actions
   */
  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: '16px 24px',
        gap: '8px',
      },
    },
  },

  /**
   * MuiAppBar - App bar component
   */
  MuiAppBar: {
    defaultProps: {
      elevation: 2,
    },
    styleOverrides: {
      root: {
        backgroundImage: 'none',
      },
    },
  },

  /**
   * MuiDrawer - Drawer component
   */
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRadius: 0,
      },
    },
  },

  /**
   * MuiAlert - Alert component
   */
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: borderRadius.md,
        fontWeight: 500,
      },
      standardSuccess: {
        backgroundColor: 'rgba(94, 140, 106, 0.14)',
        color: '#7C9A74',
      },
      standardError: {
        backgroundColor: 'rgba(182, 107, 91, 0.14)',
        color: '#CC8171',
      },
      standardWarning: {
        backgroundColor: 'rgba(176, 141, 87, 0.14)',
        color: '#C6A26B',
      },
      standardInfo: {
        backgroundColor: 'rgba(111, 145, 179, 0.14)',
        color: '#6F91B3',
      },
    },
  },

  /**
   * MuiTooltip - Tooltip component
   */
  MuiTooltip: {
    defaultProps: {
      arrow: true,
    },
    styleOverrides: {
      tooltip: {
        borderRadius: borderRadius.sm,
        fontSize: '0.75rem',
        padding: '8px 12px',
      },
    },
  },

  /**
   * MuiTab - Tab component
   * Focus: Inset outline (negative offset to stay within tab bounds)
   * Touch: Minimum 48px height, 44px width (WCAG 2.5.5)
   */
  MuiTab: {
    styleOverrides: {
      root: {
        color: '#B8B0A4',
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.875rem',
        // Touch target (WCAG 2.5.5)
        minHeight: 48,
        minWidth: MIN_TOUCH_TARGET,
        padding: '12px 16px',
        // Focus styles - inset for tabs (WCAG 2.4.7)
        '&:focus-visible': {
          outline: `${FOCUS_WIDTH}px solid ${FOCUS_COLOR}`,
          outlineOffset: `-${FOCUS_OFFSET}px`,
        },
        '&:focus:not(:focus-visible)': {
          outline: 'none',
        },
        '&.Mui-selected': {
          color: '#6F91B3',
        },
      },
    },
  },

  /**
   * MuiTabs - Tab group component
   * Uses operational blue for active indicators on dark surfaces.
   */
  MuiTabs: {
    styleOverrides: {
      indicator: {
        backgroundColor: '#6F91B3',
      },
    },
  },

  /**
   * MuiLinearProgress - Progress bar
   * Finer bars with subtler track for refined, analytical aesthetic
   */
  MuiLinearProgress: {
    styleOverrides: {
      root: {
        height: 4,
        borderRadius: 2,
        backgroundColor: 'rgba(247, 244, 238, 0.08)',
      },
      bar: {
        borderRadius: 2,
      },
    },
  },

  /**
   * MuiTableCell - Table cell
   * Subtler row separators for cleaner data tables
   */
  MuiTableCell: {
    styleOverrides: {
      root: {
        padding: '12px 16px',
        borderBottomColor: 'rgba(247, 244, 238, 0.10)',
      },
      head: {
        fontWeight: 600,
        backgroundColor: '#131D27',
        borderBottomColor: 'rgba(247, 244, 238, 0.10)',
      },
    },
  },

  /**
   * MuiBreadcrumbs - Breadcrumbs component
   */
  MuiBreadcrumbs: {
    styleOverrides: {
      separator: {
        marginLeft: '8px',
        marginRight: '8px',
      },
    },
  },

  /**
   * MuiIconButton - Icon button
   * Focus: Standard outline, 2px offset
   * Touch: Minimum 44px size (WCAG 2.5.5)
   */
  MuiIconButton: {
    styleOverrides: {
      root: {
        borderRadius: borderRadius.sm,
        // Touch target (WCAG 2.5.5)
        minWidth: MIN_TOUCH_TARGET,
        minHeight: MIN_TOUCH_TARGET,
        padding: 10, // Centers 24px icon in 44px target
        // Focus styles (WCAG 2.4.7)
        '&:focus-visible': {
          outline: `${FOCUS_WIDTH}px solid ${FOCUS_COLOR}`,
          outlineOffset: `${FOCUS_OFFSET}px`,
        },
        '&:focus:not(:focus-visible)': {
          outline: 'none',
        },
      },
      sizeSmall: {
        minWidth: 36,
        minHeight: 36,
        padding: 6,
      },
      sizeLarge: {
        minWidth: 48,
        minHeight: 48,
        padding: 12,
      },
    },
  },

  /**
   * MuiSwitch - Switch component
   * Focus: Outline on the switch base
   */
  MuiSwitch: {
    styleOverrides: {
      root: {
        padding: '8px',
        // Focus styles (WCAG 2.4.7)
        '& .MuiSwitch-switchBase:focus-visible': {
          outline: `${FOCUS_WIDTH}px solid ${FOCUS_COLOR}`,
          outlineOffset: `${FOCUS_OFFSET}px`,
          borderRadius: '50%',
        },
        '& .MuiSwitch-switchBase:focus:not(:focus-visible)': {
          outline: 'none',
        },
      },
      thumb: {
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.2)',
      },
    },
  },

  /**
   * MuiListItem - List item
   */
  MuiListItem: {
    styleOverrides: {
      root: {
        borderRadius: borderRadius.sm,
        marginBottom: '4px',
        '&.Mui-selected': {
          backgroundColor: 'rgba(176, 141, 87, 0.12)',
          '&:hover': {
            backgroundColor: 'rgba(176, 141, 87, 0.14)',
          },
        },
      },
    },
  },

  /**
   * MuiListItemButton - List item button
   * Focus: Inset outline to stay within list bounds
   * Touch: Minimum 44px height (WCAG 2.5.5)
   */
  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: borderRadius.sm,
        // Touch target (WCAG 2.5.5)
        minHeight: MIN_TOUCH_TARGET,
        paddingTop: 10,
        paddingBottom: 10,
        '&.Mui-selected': {
          backgroundColor: 'rgba(176, 141, 87, 0.12)',
          '&:hover': {
            backgroundColor: 'rgba(176, 141, 87, 0.14)',
          },
        },
        // Focus styles - inset for list items (WCAG 2.4.7)
        '&:focus-visible': {
          outline: `${FOCUS_WIDTH}px solid ${FOCUS_COLOR}`,
          outlineOffset: `-${FOCUS_OFFSET}px`,
        },
        '&:focus:not(:focus-visible)': {
          outline: 'none',
        },
      },
    },
  },

  /**
   * MuiLink - Link component
   * Focus: Standard outline with small border-radius
   */
  MuiLink: {
    styleOverrides: {
      root: {
        color: '#6F91B3',
        // Focus styles (WCAG 2.4.7)
        '&:focus-visible': {
          outline: `${FOCUS_WIDTH}px solid ${FOCUS_COLOR}`,
          outlineOffset: `${FOCUS_OFFSET}px`,
          borderRadius: '2px',
        },
        '&:hover': {
          color: '#8AA8C6',
        },
        '&:focus:not(:focus-visible)': {
          outline: 'none',
        },
      },
    },
  },

  /**
   * MuiMenuItem - Menu item
   * Focus: Background highlight instead of outline
   * Touch: Minimum 44px height (WCAG 2.5.5)
   */
  MuiMenuItem: {
    styleOverrides: {
      root: {
        // Touch target (WCAG 2.5.5)
        minHeight: MIN_TOUCH_TARGET,
        paddingTop: 10,
        paddingBottom: 10,
        // Focus styles - background highlight for menus (WCAG 2.4.7)
        '&:focus-visible': {
          outline: 'none',
          backgroundColor: 'rgba(111, 145, 179, 0.14)',
        },
        '&:focus:not(:focus-visible)': {
          outline: 'none',
        },
      },
    },
  },

  /**
   * MuiSelect - Select dropdown
   * Focus: Outline on the select element
   */
  MuiSelect: {
    styleOverrides: {
      select: {
        // Focus styles (WCAG 2.4.7)
        '&:focus-visible': {
          outline: `${FOCUS_WIDTH}px solid ${FOCUS_COLOR}`,
          outlineOffset: `${FOCUS_OFFSET}px`,
          borderRadius: borderRadius.sm,
        },
        '&:focus:not(:focus-visible)': {
          outline: 'none',
        },
      },
    },
  },

  /**
   * MuiCheckbox - Checkbox component
   * Focus: Outline around the checkbox
   * Touch: 44px touch area (WCAG 2.5.5)
   */
  MuiCheckbox: {
    styleOverrides: {
      root: {
        // Touch target (WCAG 2.5.5)
        padding: 10, // Creates 44px touch area (24px icon + 10px*2 padding)
        // Focus styles (WCAG 2.4.7)
        '&:focus-visible': {
          outline: `${FOCUS_WIDTH}px solid ${FOCUS_COLOR}`,
          outlineOffset: `${FOCUS_OFFSET}px`,
        },
        '&:focus:not(:focus-visible)': {
          outline: 'none',
        },
      },
    },
  },

  /**
   * MuiRadio - Radio button component
   * Focus: Outline around the radio
   * Touch: 44px touch area (WCAG 2.5.5)
   */
  MuiRadio: {
    styleOverrides: {
      root: {
        // Touch target (WCAG 2.5.5)
        padding: 10, // Creates 44px touch area (24px icon + 10px*2 padding)
        // Focus styles (WCAG 2.4.7)
        '&:focus-visible': {
          outline: `${FOCUS_WIDTH}px solid ${FOCUS_COLOR}`,
          outlineOffset: `${FOCUS_OFFSET}px`,
        },
        '&:focus:not(:focus-visible)': {
          outline: 'none',
        },
      },
    },
  },

  /**
   * MuiButtonBase - Base button component (fallback for all buttons)
   * Ensures all interactive elements have focus visible
   */
  MuiButtonBase: {
    styleOverrides: {
      root: {
        // Focus styles fallback (WCAG 2.4.7)
        '&:focus-visible': {
          outline: `${FOCUS_WIDTH}px solid ${FOCUS_COLOR}`,
          outlineOffset: `${FOCUS_OFFSET}px`,
        },
        '&:focus:not(:focus-visible)': {
          outline: 'none',
        },
      },
    },
  },
};
